import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import en from '@angular/common/locales/en';
import pl from '@angular/common/locales/pl';
import uk from '@angular/common/locales/uk';
import { registerLocaleData } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import LocalStorageService, {
  LocalStorageKey,
} from '@shared/services/local-storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { AuthService } from '@app/modules/components/auth/auth.service';
import MarketingAttributionApiService from '@shared/services/marketing-attribution/marketing-attribution-api.service';
import { AttributionType } from '@shared/services/marketing-attribution/marketing-attribution.types';
import { AppRoutesDefinitions } from '@app/app.routes';
import { RealtimeNotificationsService } from '@shared/services/realtime-notifications.service';
import { SessionEventsService } from '@shared/services/session-events.service';
import { MathNotificationToastComponent } from '@shared/components/math-notification-toast/math-notification-toast.component';
import { PushMessagingService } from '@shared/services/push/push-messaging.service';
import { PushOptInService } from '@shared/services/push/push-opt-in.service';
import { AppUpdateToastComponent } from '@shared/components/app-update-toast/app-update-toast.component';
import { AppUpdateService } from '@shared/services/app-update/app-update.service';
import { PromocodeCaptureService } from '@shared/services/promocode-capture/promocode-capture.service';
import { PROMOCODE_QUERY_PARAM } from '@shared/services/promocode-capture/promocode-capture.constants';
import { DEFAULT_LANGUAGE } from '@shared/constants';
import { BootSplashService } from '@shared/services/boot-splash/boot-splash.service';

const QUIZ_PATH = `/${AppRoutesDefinitions.PUBLIC}/${AppRoutesDefinitions.QUIZ}/`;

registerLocaleData(en);
registerLocaleData(pl);
registerLocaleData(uk);

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-root',
  templateUrl: './app.component.html',
  // Root component must be non-standalone to bootstrap via AppModule (NG6009).
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AppComponent implements OnInit {
  @ViewChild(MathNotificationToastComponent, { static: true })
  private readonly notificationToast!: MathNotificationToastComponent;

  @ViewChild(AppUpdateToastComponent, { static: true })
  private readonly appUpdateToast!: AppUpdateToastComponent;

  constructor(
    private readonly translateService: TranslateService,
    private readonly localStorageService: LocalStorageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly destroyRef: DestroyRef,
    private readonly authService: AuthService,
    private readonly marketingAttributionApiService: MarketingAttributionApiService,
    private readonly router: Router,
    private readonly realtimeNotificationsService: RealtimeNotificationsService,
    private readonly sessionEventsService: SessionEventsService,
    private readonly pushMessagingService: PushMessagingService,
    private readonly pushOptInService: PushOptInService,
    private readonly appUpdateService: AppUpdateService,
    private readonly promocodeCaptureService: PromocodeCaptureService,
    private readonly bootSplashService: BootSplashService,
  ) {
    this.bootSplashService.hideAfterFirstNavigation();
    this.translateService.use(
      this.localStorageService.getItem(LocalStorageKey.UI_LANGUAGE) ??
        DEFAULT_LANGUAGE.toLowerCase(),
    );

    this.trackLanguageChangeInRoute();
    this.trackUtmAttribution();
    this.capturePromocodeFromRoute();
  }

  public ngOnInit(): void {
    this.realtimeNotificationsService.start(this.notificationToast.templateRef);
    this.sessionEventsService.start();
    this.startPushNotifications();
    this.appUpdateService.start(this.appUpdateToast.templateRef);
  }

  private startPushNotifications(): void {
    this.pushOptInService.listenForInstallPrompt();

    void this.pushMessagingService.init().then(() => {
      if (this.authService.isLoggedIn()) {
        void this.pushMessagingService.onLogin();
      }
    });

    this.authService.authConnectionChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((connected) => {
        if (connected) {
          void this.pushMessagingService.onLogin();
        }
      });
  }

  private capturePromocodeFromRoute(): void {
    this.activatedRoute.queryParams
      .pipe(
        map((params) => params[PROMOCODE_QUERY_PARAM] as string | undefined),
        filter((code): code is string => !!code),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((code) => this.promocodeCaptureService.capture(code));
  }

  private trackUtmAttribution(): void {
    this.activatedRoute.queryParams
      .pipe(
        filter(
          (params) =>
            this.authService.isLoggedIn() &&
            !this.router.url.startsWith(QUIZ_PATH) &&
            !!params.utm_source &&
            !!params.utm_medium &&
            !!params.utm_campaign,
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((params) => {
        this.marketingAttributionApiService
          .createAttribution({
            utmSource: params.utm_source,
            utmMedium: params.utm_medium,
            utmCampaign: params.utm_campaign,
            utmContent: params.utm_content,
            utmTerm: params.utm_term,
            utmPartner: params.utm_partner,
            googleGclid: params.gclid,
            tiktokTtclid: params.ttclid,
            admitadUid: params.admitad_uid,
            pageUrl: window.location.href,
            referrerUrl: document?.referrer || undefined,
            placement: params.placement,
            siteSourceName: params.site_source_name,
            attributionType: AttributionType.RETURN_VISIT,
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.router.navigate([], {
              queryParams: {
                utm_source: null,
                utm_medium: null,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                utm_partner: null,
                gclid: null,
                ttclid: null,
                admitad_uid: null,
                placement: null,
                site_source_name: null,
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          });
      });
  }

  // todo: find proper way to change lang. I don't like to do it here
  private trackLanguageChangeInRoute() {
    this.activatedRoute.queryParams
      .pipe(
        distinctUntilChanged(),
        filter(
          (params) =>
            !!params.language &&
            params.language.toLowerCase() !==
              this.localStorageService.getItem(LocalStorageKey.UI_LANGUAGE),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((params) => {
        const routeLanguage = params.language;
        this.localStorageService.setItem(
          LocalStorageKey.UI_LANGUAGE,
          routeLanguage.toLowerCase(),
        );
        this.translateService.use(routeLanguage.toLowerCase());
      });
  }
}
